import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { Leaderboard } from './useSeasonLeaderboard'
import { API_INDEXER_URL } from '@/config/consts'

export type GlobalLeaderboard = {
  entries?: Leaderboard[]
  total_entries: number
}

// Type definitions for the response
interface ReferralLeaderboardEntry {
  referrer: string
  code: string
  totalCount: number
}

interface ReferralLeaderboardResponse {
  leaderboard: ReferralLeaderboardEntry[]
  meta: {
    totalReferrers: number
    limit: number
  }
}

async function fetchReferralLeaderboard(): Promise<GlobalLeaderboard> {
  const response = await fetch(`${API_INDEXER_URL}/api/referrals/leaderboard`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`API request failed: ${response.statusText}`)
  }

  const result: ReferralLeaderboardResponse = await response.json()

  if (!result.leaderboard) {
    throw new Error('Invalid API response')
  }

  // Transform the API response into leaderboard format
  const referrerData = result.leaderboard

  // Sort by total count in descending order and assign ranks
  const sortedReferrers = referrerData
    .map((item) => ({
      rank: 0, // Will be set after sorting
      wallet_address: item.referrer, // Use referrer address as wallet_address
      referrals: {
        code: item.code || '',
        total: item.totalCount,
      },
      points: {
        event_points: {
          distributed: 0,
          raw: 0,
        },
        position_points: {
          distributed: 0,
          raw: 0,
        },
        referral_points: {
          distributed: 0,
          raw: 0,
        },
        total: {
          distributed: 0,
          raw: 0,
        },
      },
    }))
    .sort((a, b) => b.referrals.total - a.referrals.total)
    .map((item, index) => ({
      ...item,
      rank: index + 1,
    }))

  return {
    entries: sortedReferrers,
    total_entries: result.meta.totalReferrers,
  }
}

export function useLeaderboard(): UseQueryResult<GlobalLeaderboard, Error> {
  const data = useQuery({
    queryKey: ['leaderboard'],
    queryFn: fetchReferralLeaderboard,
    refetchOnMount: false,
  })

  return data
}
